import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { SectionCard, SectionTitle, InfoGrid, InfoItem, FullWidthItem } from '../../Section.styles';
import { RegulationsIntro, RegulationsTable, DeleteButton } from './RegulationsSection.styles';
import { ProductDetail, SpecificRequirement } from 'src/types/ProductTypes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import useIbcSpecificRequirements from '../../../../hooks/useIbcSpecificRequirements';

// Trash icon component
const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
);

interface RegulationsSectionProps {
    product: ProductDetail;
}

const RegulationsSection: React.FC<RegulationsSectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;
    const showEditUI = canEdit && isInEditMode;

    // Check if this is a product category (no trade name)
    const isProductCategory = !product.tradeName;

    // Fetch all available IBC requirements for dropdown (only when editing product categories)
    const { allIbcRequirements } = useIbcSpecificRequirements();

    // Inhibitors comment (Category 6)
    const inhibitorsComment = product.comments?.find((comment) => comment.category === 6);
    const hasInhibitors = product.category?.isInhibited && inhibitorsComment && inhibitorsComment.text && inhibitorsComment.text.trim() !== '';

    // IBC Specific Requirements - check if ibcRegulation exists (not just if array has items)
    const ibcRegulation = product.category?.regulations?.ibcRegulation;
    const originalIbcRequirements = ibcRegulation?.specificAndOperationalRequirements || [];

    // IGC Specific Requirements - check if igcRegulation exists
    const igcRegulation = product.category?.regulations?.igcRegulation;
    const igcRequirements = igcRegulation?.specificAndOperationalRequirements || [];

    // Fosfa Required Previous Acceptable Cargoes
    const fosfaCargoes = product.regulations?.numberOfLastCargoesForEpoxyTanksFosfaBan;

    // Local state for Fosfa field
    const [fosfaValue, setFosfaValue] = useState<number | undefined>(fosfaCargoes);

    // Local state for IBC requirements (for edit mode)
    const [currentIbcRequirements, setCurrentIbcRequirements] = useState<SpecificRequirement[]>(originalIbcRequirements);

    // Sync local state when product changes
    useEffect(() => {
        setFosfaValue(product.regulations?.numberOfLastCargoesForEpoxyTanksFosfaBan);
    }, [product.regulations?.numberOfLastCargoesForEpoxyTanksFosfaBan]);

    useEffect(() => {
        setCurrentIbcRequirements(originalIbcRequirements);
    }, [originalIbcRequirements]);

    // Get IBC requirements from pending changes or local state
    const ibcRequirements = useMemo(() => {
        if (showEditUI && isProductCategory && getPendingChanges) {
            const pendingChanges = getPendingChanges();
            return pendingChanges.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements || currentIbcRequirements;
        }
        return currentIbcRequirements;
    }, [showEditUI, isProductCategory, getPendingChanges, currentIbcRequirements]);

    // Filter available IBC requirements (exclude already selected ones)
    const selectableIbcRequirements = useMemo(() => {
        const selectedIds = new Set(ibcRequirements.map((r) => r.id));
        return allIbcRequirements.filter((r) => !selectedIds.has(r.id));
    }, [allIbcRequirements, ibcRequirements]);

    // Handle Fosfa field change
    const handleFosfaChange = useCallback(
        (value: number) => {
            setFosfaValue(value);

            if (!mergePendingChanges) {
                console.log('[RegulationsSection] ⚠️ mergePendingChanges is not available!');
                return;
            }

            // Update regulations in pending changes
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
            const currentRegulations = currentPendingChanges.regulations || product.regulations || {};

            const updatedRegulations = {
                regulations: {
                    ...currentRegulations,
                    numberOfLastCargoesForEpoxyTanksFosfaBan: value,
                } as any,
            };
            
            console.log('[RegulationsSection] Fosfa changed:', value);
            console.log('[RegulationsSection] Merging regulations:', updatedRegulations);
            
            mergePendingChanges(updatedRegulations);
        },
        [mergePendingChanges, getPendingChanges, product.regulations]
    );

    // Handle adding IBC requirement
    const handleAddIbcRequirement = useCallback(
        (requirementId: number) => {
            if (!requirementId || !mergePendingChanges) {
                console.log('[RegulationsSection] ⚠️ Cannot add IBC requirement - mergePendingChanges:', !!mergePendingChanges, 'requirementId:', requirementId);
                return;
            }

            const requirementToAdd = allIbcRequirements.find((r) => r.id === requirementId);
            if (!requirementToAdd) return;

            const updatedRequirements = [...ibcRequirements, requirementToAdd];
            setCurrentIbcRequirements(updatedRequirements);

            // Update pending changes
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
            const currentCategory = currentPendingChanges.category || product.category;
            const currentCategoryRegulations = (currentCategory?.regulations || {}) as any;
            const currentIbcRegulation = currentCategoryRegulations.ibcRegulation || ibcRegulation || {};

            const updatedCategory = {
                ...currentCategory,
                regulations: {
                    ...currentCategoryRegulations,
                    ibcRegulation: {
                        ...currentIbcRegulation,
                        specificAndOperationalRequirements: updatedRequirements,
                    },
                },
            };
            
            console.log('[RegulationsSection] Adding IBC requirement:', requirementToAdd);
            console.log('[RegulationsSection] Updated requirements:', updatedRequirements.map(r => r.paragraphNumber));
            console.log('[RegulationsSection] Merging category:', updatedCategory);
            
            mergePendingChanges({ category: updatedCategory });
        },
        [allIbcRequirements, ibcRequirements, mergePendingChanges, getPendingChanges, product.category, ibcRegulation]
    );

    // Handle removing IBC requirement
    const handleRemoveIbcRequirement = useCallback(
        (requirementId: number) => {
            if (!mergePendingChanges) {
                console.log('[RegulationsSection] ⚠️ mergePendingChanges is not available for remove!');
                return;
            }

            const updatedRequirements = ibcRequirements.filter((r) => r.id !== requirementId);
            setCurrentIbcRequirements(updatedRequirements);

            // Update pending changes
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
            const currentCategory = currentPendingChanges.category || product.category;
            const currentCategoryRegulations = (currentCategory?.regulations || {}) as any;
            const currentIbcRegulation = currentCategoryRegulations.ibcRegulation || ibcRegulation || {};

            const updatedCategory = {
                ...currentCategory,
                regulations: {
                    ...currentCategoryRegulations,
                    ibcRegulation: {
                        ...currentIbcRegulation,
                        specificAndOperationalRequirements: updatedRequirements,
                    },
                },
            };
            
            console.log('[RegulationsSection] Removing IBC requirement id:', requirementId);
            console.log('[RegulationsSection] Updated requirements:', updatedRequirements.map(r => r.paragraphNumber));
            console.log('[RegulationsSection] Merging category:', updatedCategory);
            
            mergePendingChanges({ category: updatedCategory });
        },
        [ibcRequirements, mergePendingChanges, getPendingChanges, product.category, ibcRegulation]
    );

    // Format requirement for display in dropdown
    const formatRequirementOption = (req: SpecificRequirement) => {
        return `${req.paragraphNumber} - ${req.paragraphHeading}`;
    };

    // Show table with headers when there are requirements or in edit mode for product category
    const showIbcTable = ibcRequirements.length > 0 || (showEditUI && isProductCategory);
    const showIgcTable = igcRequirements.length > 0;

    return (
        <SectionCard>
            <SectionTitle>Regulations Requirements</SectionTitle>

            {/* Inhibitors Section */}
            {hasInhibitors && (
                <FullWidthItem>
                    <label>Inhibitors:</label>
                    <div className="full-width-content">
                        <pre>{inhibitorsComment.text}</pre>
                    </div>
                </FullWidthItem>
            )}

            {/* Fosfa Required Previous Acceptable Cargoes */}
            {(fosfaValue || showEditUI) && (
                <InfoGrid>
                    <InfoItem>
                        <label>Fosfa Required Previous Acceptable Cargoes:</label>
                        {showEditUI ? (
                            <select
                                value={fosfaValue || ''}
                                onChange={(e) => handleFosfaChange(Number(e.target.value))}
                            >
                                <option value="">Select...</option>
                                <option value="1">1</option>
                                <option value="3">3</option>
                            </select>
                        ) : (
                            <span>{fosfaValue}</span>
                        )}
                    </InfoItem>
                </InfoGrid>
            )}

            {/* IBC Specific And Operational Requirements - show section if ibcRegulation exists */}
            {ibcRegulation && (
                <>
                    {showIbcTable ? (
                        <> <InfoItem>
                                <RegulationsIntro>IBC Specific And Operational Requirements (o):</RegulationsIntro>
                            </InfoItem>
                            <RegulationsTable className={showEditUI && isProductCategory ? 'editable' : ''}>
                                <div className="table-header">
                                    {showEditUI && isProductCategory && <span></span>}
                                    <span>Chapter</span>
                                    <span>Heading</span>
                                </div>
                                {ibcRequirements.map((requirement) => (
                                    <div className="table-row" key={requirement.id} title={requirement.text}>
                                        {showEditUI && isProductCategory && (
                                            <DeleteButton
                                                type="button"
                                                onClick={() => handleRemoveIbcRequirement(requirement.id)}
                                                title="Remove Specific Requirement"
                                            >
                                                <TrashIcon />
                                            </DeleteButton>
                                        )}
                                        <span>{requirement.paragraphNumber}</span>
                                        <span>{requirement.paragraphHeading}</span>
                                    </div>
                                ))}
                                {showEditUI && isProductCategory && selectableIbcRequirements.length > 0 && (
                                    <div className="table-footer">
                                        <select
                                            value=""
                                            onChange={(e) => handleAddIbcRequirement(Number(e.target.value))}
                                        >
                                            <option value="">Add requirement...</option>
                                            {selectableIbcRequirements.map((req) => (
                                                <option key={req.id} value={req.id}>
                                                    {formatRequirementOption(req)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </RegulationsTable>
                        </>
                    ) : (
                        <InfoGrid>
                            <InfoItem>
                                <label>IBC Specific And Operational Requirements (o):</label>
                                <span>None</span>
                            </InfoItem>
                        </InfoGrid>
                    )}
                </>
            )}

            {/* IGC Specific Requirements - show section if igcRegulation exists */}
            {igcRegulation && (
                <>
                    {showIgcTable ? (
                        <>
                            <RegulationsIntro>IGC Specific Requirements (i):</RegulationsIntro>
                            <RegulationsTable>
                                <div className="table-header">
                                    <span>Chapter</span>
                                    <span>Heading</span>
                                </div>
                                {igcRequirements.map((requirement: any) => (
                                    <div className="table-row" key={requirement.id} title={requirement.text}>
                                        <span>{requirement.paragraphNumber}</span>
                                        <span>{requirement.paragraphHeading}</span>
                                    </div>
                                ))}
                            </RegulationsTable>
                        </>
                    ) : (
                        <InfoGrid>
                            <InfoItem>
                                <label>IGC Specific Requirements (i):</label>
                                <span>None.</span>
                            </InfoItem>
                        </InfoGrid>
                    )}
                </>
            )}
        </SectionCard>
    );
};

export default RegulationsSection;
